import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Database, MagnifyingGlass, CaretLeft, CaretRight, Trash, PencilSimple, X, Check } from "@phosphor-icons/react";
import { api } from "../../lib/api";

const PAGE_SIZE = 25;

export default function AdminDbBrowser() {
    const [collections, setCollections] = useState([]);
    const [active, setActive] = useState(null);
    const [docs, setDocs] = useState([]);
    const [total, setTotal] = useState(0);
    const [skip, setSkip] = useState(0);
    const [q, setQ] = useState("");
    const [busy, setBusy] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const [editing, setEditing] = useState(null);   // doc _id being edited
    const [editText, setEditText] = useState("");

    const loadCollections = async () => {
        try {
            const r = await api.get("/admin/db/collections").then((r) => r.data);
            setCollections(r.collections || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Nepodařilo se načíst kolekce");
        }
    };
    useEffect(() => { loadCollections(); }, []);

    const loadDocs = async (coll = active, s = skip, query = q) => {
        if (!coll) return;
        setBusy(true);
        try {
            const r = await api.get(`/admin/db/${coll}/docs`, {
                params: { skip: s, limit: PAGE_SIZE, ...(query ? { q: query } : {}) },
            }).then((r) => r.data);
            setDocs(r.docs || []);
            setTotal(r.total || 0);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Nepodařilo se načíst dokumenty");
        } finally {
            setBusy(false);
        }
    };

    const openCollection = (name) => {
        setActive(name);
        setSkip(0);
        setQ("");
        setExpanded(null);
        setEditing(null);
        loadDocs(name, 0, "");
    };

    const search = () => { setSkip(0); loadDocs(active, 0, q); };

    const page = (dir) => {
        const next = Math.max(0, skip + dir * PAGE_SIZE);
        if (next >= total && dir > 0) return;
        setSkip(next);
        loadDocs(active, next, q);
    };

    const deleteDoc = async (id) => {
        if (!window.confirm(`Opravdu smazat dokument ${id} z ${active}?`)) return;
        try {
            await api.delete(`/admin/db/${active}/docs/${id}`);
            toast.success("Dokument smazán");
            loadDocs();
            loadCollections();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Mazání selhalo");
        }
    };

    const startEdit = (doc) => {
        const { _id, ...body } = doc;
        setEditing(doc._id);
        setEditText(JSON.stringify(body, null, 2));
    };

    const saveEdit = async () => {
        let body;
        try {
            body = JSON.parse(editText);
        } catch {
            toast.error("Neplatný JSON");
            return;
        }
        try {
            await api.put(`/admin/db/${active}/docs/${editing}`, { doc: body });
            toast.success("Dokument uložen");
            setEditing(null);
            loadDocs();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Uložení selhalo");
        }
    };

    // Table columns = union of top-level keys of first docs (max 6 shown)
    const columns = useMemo(() => {
        const keys = [];
        for (const d of docs.slice(0, 5)) {
            for (const k of Object.keys(d)) {
                if (k !== "_id" && !keys.includes(k)) keys.push(k);
            }
        }
        return keys.slice(0, 5);
    }, [docs]);

    const cellVal = (v) => {
        if (v == null) return "—";
        if (typeof v === "object") return Array.isArray(v) ? `[${v.length}]` : "{…}";
        return String(v).slice(0, 48);
    };

    return (
        <div className="grid md:grid-cols-[240px_1fr] gap-3" data-testid="admin-db-browser">
            {/* Collections sidebar */}
            <div className="border border-white/10 bg-[#0d0d0d] p-2 max-h-[70vh] overflow-y-auto space-y-0.5">
                <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 px-2 py-1 flex items-center gap-1.5">
                    <Database size={11} weight="fill" className="text-fa-mint" />
                    Kolekce ({collections.length})
                </div>
                {collections.map((c) => (
                    <button
                        key={c.name}
                        type="button"
                        onClick={() => openCollection(c.name)}
                        data-testid={`db-coll-${c.name}`}
                        className={`w-full text-left px-2 py-1 flex items-center justify-between gap-2 font-mono text-[10px] ${active === c.name ? "bg-fa-yellow text-black font-bold" : "text-white/70 hover:bg-white/5"}`}
                    >
                        <span className="truncate">{c.name}</span>
                        <span className="tabular-nums opacity-60">{c.count.toLocaleString("cs")}</span>
                    </button>
                ))}
            </div>

            {/* Docs table */}
            <div className="space-y-2 min-w-0">
                {!active ? (
                    <div className="border border-dashed border-white/15 bg-[#0d0d0d] p-8 text-center font-mono text-[11px] text-white/45">
                        Vyber kolekci vlevo.
                    </div>
                ) : (
                    <>
                        <div className="flex items-center gap-2 flex-wrap">
                            <div className="relative flex-1 min-w-[200px]">
                                <MagnifyingGlass size={11} weight="bold" className="absolute left-2 top-1/2 -translate-y-1/2 text-white/40" />
                                <input
                                    type="text"
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && search()}
                                    placeholder={`Hledat v ${active}…`}
                                    data-testid="db-search-input"
                                    className="w-full bg-black/40 border border-white/15 pl-7 pr-2 py-1.5 font-mono text-[11px] text-white focus:outline-none focus:border-fa-yellow"
                                />
                            </div>
                            <button
                                type="button"
                                onClick={search}
                                data-testid="db-search-btn"
                                className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-fa-yellow/50 text-fa-yellow hover:bg-fa-yellow hover:text-black"
                            >
                                Hledat
                            </button>
                            <span className="font-mono text-[10px] text-white/50 tabular-nums ml-auto" data-testid="db-total">
                                {skip + 1}–{Math.min(skip + PAGE_SIZE, total)} z {total.toLocaleString("cs")}
                            </span>
                            <button type="button" onClick={() => page(-1)} disabled={skip === 0 || busy} data-testid="db-prev" className="border border-white/15 p-1.5 text-white/60 hover:text-white disabled:opacity-30">
                                <CaretLeft size={12} weight="bold" />
                            </button>
                            <button type="button" onClick={() => page(1)} disabled={skip + PAGE_SIZE >= total || busy} data-testid="db-next" className="border border-white/15 p-1.5 text-white/60 hover:text-white disabled:opacity-30">
                                <CaretRight size={12} weight="bold" />
                            </button>
                        </div>

                        <div className="border border-white/10 bg-[#0d0d0d] overflow-x-auto">
                            <table className="w-full min-w-[640px]">
                                <thead className="bg-black/40">
                                    <tr>
                                        <th className="text-left font-mono text-[9px] uppercase tracking-widest text-white/50 px-2 py-2">_id</th>
                                        {columns.map((c) => (
                                            <th key={c} className="text-left font-mono text-[9px] uppercase tracking-widest text-white/50 px-2 py-2">{c}</th>
                                        ))}
                                        <th className="text-right font-mono text-[9px] uppercase tracking-widest text-white/50 px-2 py-2">Akce</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {busy && (
                                        <tr><td colSpan={columns.length + 2} className="px-3 py-5 text-center font-mono text-[10px] italic text-white/40">Načítám…</td></tr>
                                    )}
                                    {!busy && docs.length === 0 && (
                                        <tr><td colSpan={columns.length + 2} className="px-3 py-5 text-center font-mono text-[10px] italic text-white/40">Žádné dokumenty</td></tr>
                                    )}
                                    {!busy && docs.map((d) => (
                                        <React.Fragment key={d._id}>
                                            <tr
                                                className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer"
                                                onClick={() => setExpanded(expanded === d._id ? null : d._id)}
                                                data-testid={`db-row-${d._id}`}
                                            >
                                                <td className="px-2 py-1.5 font-mono text-[9px] text-white/45 tabular-nums">{String(d._id).slice(-8)}</td>
                                                {columns.map((c) => (
                                                    <td key={c} className="px-2 py-1.5 font-mono text-[10px] text-white/75 truncate max-w-[180px]">{cellVal(d[c])}</td>
                                                ))}
                                                <td className="px-2 py-1.5 text-right whitespace-nowrap">
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); startEdit(d); }}
                                                        title="Editovat"
                                                        data-testid={`db-edit-${d._id}`}
                                                        className="text-fa-sky/70 hover:text-fa-sky p-1"
                                                    >
                                                        <PencilSimple size={12} weight="bold" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); deleteDoc(d._id); }}
                                                        title="Smazat"
                                                        data-testid={`db-delete-${d._id}`}
                                                        className="text-red-300/70 hover:text-red-300 p-1"
                                                    >
                                                        <Trash size={12} weight="bold" />
                                                    </button>
                                                </td>
                                            </tr>
                                            {expanded === d._id && editing !== d._id && (
                                                <tr className="bg-black/40">
                                                    <td colSpan={columns.length + 2} className="px-3 py-2">
                                                        <pre className="font-mono text-[10px] text-fa-mint/90 whitespace-pre-wrap break-all max-h-72 overflow-y-auto" data-testid="db-doc-json">
                                                            {JSON.stringify(d, null, 2)}
                                                        </pre>
                                                    </td>
                                                </tr>
                                            )}
                                            {editing === d._id && (
                                                <tr className="bg-black/40">
                                                    <td colSpan={columns.length + 2} className="px-3 py-2 space-y-2">
                                                        <textarea
                                                            value={editText}
                                                            onChange={(e) => setEditText(e.target.value)}
                                                            rows={12}
                                                            data-testid="db-edit-textarea"
                                                            className="w-full bg-black/60 border border-fa-sky/40 p-2 font-mono text-[10px] text-white focus:outline-none focus:border-fa-sky"
                                                        />
                                                        <div className="flex items-center gap-2">
                                                            <button type="button" onClick={saveEdit} data-testid="db-save-btn" className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 bg-fa-mint text-black font-bold hover:bg-fa-mint/80 flex items-center gap-1">
                                                                <Check size={11} weight="bold" /> Uložit
                                                            </button>
                                                            <button type="button" onClick={() => setEditing(null)} data-testid="db-cancel-btn" className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-white/20 text-white/60 hover:text-white flex items-center gap-1">
                                                                <X size={11} weight="bold" /> Zrušit
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
